<script setup lang="ts">
import { useXPixel } from "~/composables/useXPixel";
import { getGFALFeeFromTotalPurchasePrice } from "~/utils/number.utils";
import {
  getPurchaseEligibility,
  PURCHASE_UNAVAILABLE_REASON_LOW_VALUE,
} from "~/utils/purchase-eligibility.utils";
import { PurchaseType } from "~~/shared/types/analyticspurchase.enums";
//const { proxy } = useScriptXPixel()
const { loadXPixel, trackEventXPixel } = useXPixel();
onMounted(() => {
  loadXPixel();
});
const config = useRuntimeConfig();
const xPixelTrackingEnabledForSetId = String(
  config.public?.xPixelTrackingEnabledForSetId
);
const GFALExStore = useGFALExStore();
const { marketplaceFeesPercent, minimumPrice } = useRuntimeConfig().public;

function sendXPixelConversion_PurchaseEnd(
  vValue: number | null,
  vCurrency: string | null
) {
  if (props.item.categoryId === xPixelTrackingEnabledForSetId) {
    trackEvent("tw-on8c3-q3e1g");
    if (typeof window?.twq === "function") {
      trackEventXPixel("tw-on8c3-q3e1g", {
        value: vValue,
        currency: vCurrency,
      });
    } else {
      trackEvent("tw-on8c3-q3e1g-BLOCKED-BY-CLIENT");
      console.warn(
        "Twitter Pixel tw-on8c3-q3e1g not loaded or blocked by client."
      );
    }
  }
}

const emit = defineEmits([
  "close",
  "mintComplete",
  "isPurchased",
  "updateCollectible",
]);
const props = defineProps({
  item: {
    type: Object as PropType<BaseMarketItem>,
    required: true,
  },
  minting: {
    type: Boolean,
    required: false,
    default: false,
  },
  isLimitedEdition: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const localItem = ref<BaseMarketItem>({ ...props.item });

const { t } = useI18n();
const { toaster } = useToaster();
const { refreshUserFinancials } = useBalanceRefresh();
const {
  create: itemsCreate,
  sign: itemsSign,
  getItemById,
  previewCreate,
} = useItemsController();
const { create: collectiblesCreate, sign: collectiblesSign } =
  useCollectiblesController();
const { getUserBalance, createMint, signMint } = useUserController();
const { trackEvent } = useGAnalytics();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();
const currentRoute = computed(() => {
  if (route.path.includes("store")) return "store";
  if (route.path.includes("market")) return "market";
  if (route.path.includes("collectible")) return "collectible";
  if (route.path.includes("mint")) return "mint";
  return "store";
});
const queueInfo = ref<{ id?: string; position?: number }>({});
const purchaseConfig: PurchaseConfig = {
  store: {
    create: itemsCreate,
    sign: itemsSign,
    identifier: { id: props.item.id, merchantId: props.item.merchantId },
    router: `/store/${route.params.merchant}`,
  },
  market: {
    create: collectiblesCreate,
    sign: collectiblesSign,
    identifier: {
      id: props.item.id,
      ownerId: props.item.gfalId,
      price: props.item.price,
      level: props.item.level,
    },
    router: `/market/${route.params.merchant}`,
  },
  collectible: {
    create: collectiblesCreate,
    sign: collectiblesSign,
    identifier: {
      id: props.item.id,
      ownerId: props.item.gfalId,
      price: props.item.price,
    },
    router: `/user/${route.params.id}`,
  },
  mint: {
    create: createMint,
    sign: signMint,
    identifier: { id: props.item.id },
    router: `/collection/${props.item.merchantId}`,
  },
};

const isModalBuyLoginShown = ref<boolean>(false);
const isModalBuyBeginShown = ref<boolean>(false);
const isModal2FAShown = ref<boolean>(false);
const isModalBuyEndShown = ref<boolean>(false);
const isModalLocked = ref<boolean>(false);
const isModalLoaderShown = ref<boolean>(false);
const isModalQueueShown = ref<boolean>(false);
const isSoldOut = ref<boolean>(false);
const success = ref<boolean>(false);
const hasUpdated = ref<boolean>(false);
const isLocked = ref<boolean>(false);
const isProcessing = ref<boolean>(false);
const isInsufficientPrice = ref<boolean>(false);
const isMismatch = ref<boolean>(false);
const collectibleId = ref<string>("");
const isBuyingFromQueue = ref<boolean>(false);
const isPurchaseBlockedByFeeCoverage = computed(() => {
  const shouldEnforceRule =
    currentRoute.value === "market" || currentRoute.value === "collectible";
  if (!shouldEnforceRule) return false;

  const eligibility = getPurchaseEligibility(
    {
      priceGFAL: localItem.value.price?.GFAL,
      isPurchasable: localItem.value.isPurchasable,
      purchaseBlockReason: localItem.value.purchaseBlockReason,
    },
    {
      marketplaceFeesPercent: Number(marketplaceFeesPercent),
      minimumPriceUSD: Number(minimumPrice),
      gfalExRate: Number(GFALExStore.GFALExRate),
    }
  );

  return (
    !eligibility.isPurchasable &&
    eligibility.purchaseBlockReason === PURCHASE_UNAVAILABLE_REASON_LOW_VALUE
  );
});

const payload = ref<Payload>({
  merchantId: "",
  purchaseId: "",
  operationHash: "",
  code: "",
});

const get2FA = (signPayload: Payload) => {
  payload.value.merchantId = signPayload.merchantId;
  payload.value.purchaseId = signPayload.purchaseId;
  payload.value.operationHash = signPayload.operationHash;
  signPurchase();
};

const getQueue2FA = () => {
  required2FA();
};

const soldOutFromQueue = () => {
  isModalQueueShown.value = false;
  isModal2FAShown.value = false;
  isSoldOut.value = true;
  isLocked.value = true;
  toaster("error");
  showFailModal();
};

const signOrPurchase = async (code: string = "") => {
  if (props.isLimitedEdition) {
    await confirmPurchase(code);
  } else {
    await signPurchase(code);
  }
};

const confirmPurchase = async (code: string = "") => {
  try {
    const purchaseResponse = await purchaseConfig[currentRoute.value].create(
      purchaseConfig[currentRoute.value].identifier,
      code
    );
    isModal2FAShown.value = false;
    isModalBuyBeginShown.value = false;
    switch (purchaseResponse.statusCode) {
      case 200:
        payload.value.purchaseId = purchaseResponse.payload.id;
        payload.value.operationHash = purchaseResponse.payload.operationHash;
        payload.value.code = code;
        isModalLoaderShown.value = true;
        signPurchase(code);
        break;
    }
  } catch (error: any) {
    isModal2FAShown.value = false;
    isModalBuyBeginShown.value = false;
    switch (error.statusCode) {
      case 404:
        purchaseFailSoldOutLocked(error);
        break;
      case 423:
        enterQueueFromLocked(error.payload?.queuePosition);
        break;
      case 433:
        purchaseFailMismatch(error);
        break;
      default:
        purchaseFail(error);
    }
  }
};

const signPurchase = async (code: string = "") => {
  trackEvent("buy_2FA_submitted", {
    itemId: `${props.item.id}`,
    itemCategoryId: `${props.item.categoryId}`,
    itemPrice: `${props.item.price?.GFAL ?? null}`,
  });
  try {
    const signResponse = await purchaseConfig[currentRoute.value].sign(
      purchaseConfig[currentRoute.value].identifier,
      payload.value.purchaseId,
      payload.value.operationHash,
      code
    );
    isModal2FAShown.value = false;
    isModalQueueShown.value = false;
    isModalBuyBeginShown.value = false;
    switch (signResponse.statusCode) {
      case 200:
        purchaseSuccess(signResponse.payload);
        break;
      case 202:
        purchasePending();
        break;
      case 406:
        purchaseFailMinimumPrice(signResponse);
        break;
      case 409:
        if (currentRoute.value === "store" && !props.item.gfalId) {
          handleItemLocked();
        } else {
          handleCollectibleUpdate();
        }
        break;
      case 423:
        handleItemLocked();
        break;
      case 433:
        purchaseFailMismatch(signResponse);
        break;
      default:
        purchaseFail(signResponse);
    }
  } catch (error: any) {
    if (error.statusCode === 400) {
      required2FA();
      return;
    }
    if (error.statusCode === 406) {
      purchaseFailMinimumPrice(error);
      return;
    }
    if (error.statusCode === 409) {
      if (currentRoute.value === "store" && !props.item.gfalId) {
        handleItemLocked();
      } else {
        handleCollectibleUpdate();
      }
      return;
    }
    if (error.statusCode === 423) {
      handleItemLocked();
      return;
    }
    if (error.statusCode === 433) {
      purchaseFailMismatch(error);
      return;
    }
    purchaseFail(error);
  }
};

const getPurchaseType = () => {
  if (props.minting) return PurchaseType.MINT;
  if (props.item.gfalId) return PurchaseType.PLAYERS_MARKET_BUY_FEE;
  if (props.item.level) return PurchaseType.ELITE_ITEM;
  return PurchaseType.SHOP;
};

const purchaseSuccess = (signRes: Purchase) => {
  collectibleId.value = signRes.collectibleId;
  const itemName =
    t(`${props.item.translationId}.title`) +
    " " +
    t(`${props.item.translationId}.subtitle`);
  var itemPriceGFAL = props.item.price?.GFAL ?? null;
  var itemPriceUSD = props.item.price?.USD ?? null;

  if (itemPriceUSD !== null && itemPriceGFAL !== null) {
    if (props.item.gfalId) {
      itemPriceGFAL = getGFALFeeFromTotalPurchasePrice(itemPriceGFAL);
      itemPriceUSD = itemPriceGFAL * GFALExStore.GFALExRate;
    }
    const purchasePayload = {
      transaction_id: signRes.id,
      currency: "USD",
      value: itemPriceUSD,
      purchase_type: getPurchaseType(),
      items: [
        {
          item_id: `${props.item.id}`,
          item_name: itemName,
          item_category: `${props.item.categoryId}`,
          price: itemPriceUSD,
          quantity: 1,
        },
      ],
    };
    trackEvent("purchase", purchasePayload);
  }
  sendXPixelConversion_PurchaseEnd(itemPriceGFAL, "GFAL");
  if (props.item.price) {
    let itemPrice = roundNum(props.item.price.GFAL) + " GFAL";
    if (localItem.value.price) {
      itemPrice = roundNum(localItem.value.price.GFAL) + " GFAL";
    }
    toaster(
      "success",
      currentRoute.value === "mint" ? "mintSuccess" : "purchaseSuccess",
      {
        item: itemName,
        itemPrice: itemPrice,
      }
    );
    refreshUserFinancials();
    success.value = true;
    isModalLocked.value = false;
    isModalBuyEndShown.value = true;
  } else {
    success.value = false;
    toaster("error");
  }
};

const purchasePending = () => {
  trackEvent("buy_pending", {
    itemId: `${props.item.id}`,
    itemCategoryId: `${props.item.categoryId}`,
    itemPrice: `${props.item.price?.GFAL ?? null}`,
  });
  refreshUserFinancials();
  isProcessing.value = true;
  isModalLocked.value = false;
  isModalBuyEndShown.value = true;
};

const purchaseFailMinimumPrice = (res: GFALResponse<Purchase>) => {
  isModal2FAShown.value = false;
  trackEvent("buy_fail_minimum_price", {
    itemId: `${props.item.id}`,
    itemCategoryId: `${props.item.categoryId}`,
    itemPrice: `${props.item.price?.GFAL ?? null}`,
    error: res.message,
  });
  toaster("error", "purchaseFailMinimumPrice");
  isInsufficientPrice.value = true;
  showFailModal();
};

const purchaseFailMismatch = (res: GFALResponse<Purchase>) => {
  isModal2FAShown.value = false;
  trackEvent("buy_fail_mismatch", {
    itemId: `${props.item.id}`,
    itemCategoryId: `${props.item.categoryId}`,
    itemPrice: `${props.item.price?.GFAL ?? null}`,
    error: res.message,
  });
  toaster("error");
  isMismatch.value = true;
  showFailModal();
};

const purchaseFailSoldOutLocked = (res: GFALResponse<Purchase>) => {
  isModal2FAShown.value = false;
  trackEvent("buy_fail_sold_out", {
    itemId: `${props.item.id}`,
    itemCategoryId: `${props.item.categoryId}`,
    itemPrice: `${props.item.price?.GFAL ?? null}`,
    error: res.message,
  });
  toaster("error");
  isSoldOut.value = true;
  isLocked.value = true;
  showFailModal();
};

const purchaseFail = (res: GFALResponse<Purchase>) => {
  trackEvent("buy_fail", {
    itemId: `${props.item.id}`,
    itemCategoryId: `${props.item.categoryId}`,
    itemPrice: `${props.item.price?.GFAL ?? null}`,
    error: res.message,
  });
  toaster("error");
  showFailModal();
};

const showFailModal = () => {
  isModal2FAShown.value = false;
  isModalLocked.value = false;
  isModalBuyBeginShown.value = false;
  isModal2FAShown.value = false;
  isModalQueueShown.value = false;
  isModalBuyEndShown.value = true;
};

const required2FA = () => {
  isModalBuyBeginShown.value = false;
  trackEvent("buy_2FA_requested", {
    itemId: `${props.item.id}`,
    itemCategoryId: `${props.item.categoryId}`,
  });
  toaster("warn", "code2FARequired");
  isModal2FAShown.value = true;
};

const handleCollectibleUpdate = () => {
  isModal2FAShown.value = false;
  isModalBuyBeginShown.value = false;
  hasUpdated.value = true;
  isModalBuyEndShown.value = true;
};

const handleItemLocked = () => {
  isModal2FAShown.value = false;
  isModalLoaderShown.value = false;
  isModalBuyBeginShown.value = false;
  isLocked.value = true;
  isModalBuyEndShown.value = true;
};

const retry = () => {
  isModalBuyEndShown.value = false;
  isModalBuyBeginShown.value = true;
};

const closeAll = () => {
  emit("close");
};

const onCloseEnd = () => {
  if (isModalLocked.value) {
    router.go(0);
    return;
  }
  if (!isModalBuyEndShown.value) emit("close");
  else {
    router.push(purchaseConfig[currentRoute.value].router);
    emit("close");
  }
};

const layoutModalWidth = computed(() => {
  if (isModalBuyLoginShown.value || isModalBuyEndShown.value) {
    return "medium";
  } else if (isModal2FAShown.value) {
    return "medium";
  } else if (isModalBuyBeginShown.value || isModalQueueShown.value) {
    return "large";
  } else {
    return "default";
  }
});

watch(
  () => userStore.authToken,
  (newVal) => {
    if (newVal) {
      isModalBuyLoginShown.value = false;
      getUserBalance();
      isModalBuyBeginShown.value = true;
    }
  }
);

const checkItemSoldOutOrLocked = async () => {
  if (currentRoute.value === "store") {
    isModalLoaderShown.value = true;
    Promise.all([checkCollectibleSoldOut(), checkItemLocked()]);
    isModalLoaderShown.value = false;
  }
};

const checkCollectibleSoldOut = async () => {
  const { getItemById } = useItemsController();
  try {
    const item = await getItemById(props.item.id, props.item.merchantId);
    if (item?.number === 0) isSoldOut.value = true;
  } catch (error: any) {
    if (error.statusCode === 404) isSoldOut.value = true;
  }
};

const beginPurchaseFromQueue = async () => {
  const newItem = await getItemById(props.item.id, props.item.merchantId);
  localItem.value = {
    ...localItem.value,
    ...newItem,
  };
  isBuyingFromQueue.value = true;
  isModalBuyBeginShown.value = true;
  isModalQueueShown.value = false;
};

const enterQueueFromLocked = (position?: number) => {
  isLocked.value = true;
  queueInfo.value = {
    position,
  };
  isModalBuyBeginShown.value = false;
  isModalLocked.value = true;
  isModalQueueShown.value = true;
};

const checkItemLocked = async () => {
  try {
    const purchaseRes = await previewCreate(
      purchaseConfig[currentRoute.value].identifier
    );
    if (purchaseRes?.statusCode === 423) throw purchaseRes;
  } catch (error: any) {
    if (error.statusCode === 423) {
      if (
        error.payload?.queueId &&
        error.payload?.queuePosition !== undefined
      ) {
        isLocked.value = true;
        queueInfo.value = {
          id: error.payload.queueId,
          position: error.payload.queuePosition,
        };
        isModalQueueShown.value = true;
      } else {
        isModalBuyBeginShown.value = false;
        isLocked.value = true;
        isModalBuyEndShown.value = true;
      }
    } else {
      isModalQueueShown.value = false;
      isModal2FAShown.value = false;
      isSoldOut.value = false;
      isLocked.value = false;
      toaster("error");
      showFailModal();
    }
  }
};

onMounted(async () => {
  if (isPurchaseBlockedByFeeCoverage.value) {
    emit("close");
    return;
  }

  if (!userStore.authToken) {
    isModalBuyLoginShown.value = true;
  } else {
    await checkItemSoldOutOrLocked();
    getUserBalance();
    if (isModalQueueShown.value) return;
    isModalBuyBeginShown.value = true;
  }
});
</script>
<template>
  <BaseLayoutModal
    :width="layoutModalWidth"
    :hide-x="isModalLocked"
    @close="onCloseEnd"
    @mouse-up="onCloseEnd">
    <template #header>
      {{
        isModalQueueShown
          ? "In Queue"
          : isModalBuyLoginShown
          ? $t("main.modalBuyLogin.loginOrSignUp")
          : $t("main.modalBuyBegin.title")
      }}
    </template>
    <template #content>
      <Transition mode="out-in">
        <ModalBuyLogin v-if="isModalBuyLoginShown" @close="closeAll" />
        <ModalBuyBegin
          v-else-if="isModalBuyBeginShown"
          :base-market-item="localItem"
          :create-purchase="purchaseConfig[currentRoute].create"
          :identifier="purchaseConfig[currentRoute].identifier"
          :minting="minting"
          :is-sold-out="isSoldOut"
          :buy-from-queue="isBuyingFromQueue"
          :is-limited-edition="isLimitedEdition"
          @get2-f-a="get2FA"
          @get-queue-2-f-a="getQueue2FA"
          @submit-purchase="signPurchase"
          @toggle-modal-lock="isModalLocked = !isModalLocked"
          @toggle-queue-modal="enterQueueFromLocked"
          @close="closeAll"
          @is-purchased="emit('isPurchased')"
          @is-updated="handleCollectibleUpdate"
          @is-locked="handleItemLocked" />
        <Modal2FA
          v-else-if="isModal2FAShown"
          @close2-f-a="emit('close')"
          @2-f-a-submitted="signOrPurchase"
          @toggle-modal-lock="isModalLocked = !isModalLocked" />
        <ModalBuyEnd
          v-else-if="isModalBuyEndShown"
          :success="success"
          :current-route="currentRoute"
          :collectible-id="collectibleId"
          :minting="minting"
          :has-updated="hasUpdated"
          :is-locked="isLocked"
          :is-processing="isProcessing"
          :is-insufficient-price="isInsufficientPrice"
          :is-mismatch="isMismatch"
          @close="onCloseEnd"
          @retry="retry"
          @update-collectible="emit('updateCollectible')" />
        <ModalLoader
          v-else-if="isModalLoaderShown"
          :title="$t('main.modalLoader.processingRequest')"
          :body-subtitle="$t('main.modalBuyBegin.loader')" />
        <ModalBuyQueue
          v-else-if="isModalQueueShown"
          :base-market-item="item"
          :queue-info="queueInfo"
          :sold-out="soldOutFromQueue"
          @beginPurchase="beginPurchaseFromQueue"
          @close="closeAll" />
      </Transition>
    </template>
  </BaseLayoutModal>
</template>

<style scoped>
.v-enter-active {
  transition: all 0.3s ease-out;
}
.v-enter-from {
  opacity: 0;
  transform: translate(0, 1rem);
}
.v-enter-to {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-active {
  transition: all 0.2s ease-in;
}
.v-leave-from {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-to {
  opacity: 0;
  transform: translate(0, 1rem);
}
@media (min-width: 640px) {
  .v-enter-from,
  .v-leave-to {
    transform: translate(0, 0) scale(0.95);
  }
  .v-enter-to,
  .v-leave-from {
    transform: translate(0, 0) scale(1);
  }
}
</style>
